import { useEffect, useMemo, useState } from "react";
import { Box, BoxProps, Tab, Tabs } from "@mui/material";
import { mdiGiftOutline } from "@mdi/js";

import AppBar from "components/AppBar";
import DataIconPlaceholder from "components/DataIconPlaceholder";
import PageLayout from "components/PageLayout";
import PageLoader from "components/PageLoader";
import useNotifier from "hooks/useNotifier";
import useBoundStore from "store";
import SecretSantaApi, { GetMyPairWishlistRes } from "api/secret-santa";
import MyPair from "./MyPair";
import MyWishlist from "./MyWishlist";

interface TabPanelProps extends BoxProps {
  "aria-labelledby": string;
  children?: React.ReactNode;
  id: string;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...rest } = props;
  return (
    <Box role='tabpanel' p={2} hidden={value !== index} {...rest}>
      {value === index && children}
    </Box>
  );
}

export interface TimelineSecretSantaProps {
  onBack?: () => void;
}

export default function TimelineSecretSanta(props: TimelineSecretSantaProps) {
  const { onBack } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [secretSanta, setSecretSanta] = useState<GetMyPairWishlistRes | null>(null);
  const [tab, setTab] = useState(0);

  const selected = useBoundStore((state) => state.selected);
  const isAppFocused = useBoundStore((state) => state.miscellaneous.isAppFocused);
  const userAuthId = useBoundStore((state) => state.user.auth?.id);
  const { timelineEvent } = selected;
  const { showNotification } = useNotifier();
  const secretSantaApi = useMemo(() => new SecretSantaApi(showNotification), [showNotification]);

  useEffect(() => {
    (async () => {
      if (timelineEvent?.event && userAuthId) {
        const { data } = await secretSantaApi.getMyPairWishlist(userAuthId, timelineEvent.event.id);
        setSecretSanta(data);
      }
      setIsLoading(false);
    })();
  }, [timelineEvent?.event?.id, userAuthId]);

  if (isLoading) {
    return (
      <PageLayout appBar={<AppBar noRadius title='Secret Santa' onBack={onBack} />}>
        <PageLoader />
      </PageLayout>
    );
  }

  if (!secretSanta) {
    return (
      <PageLayout appBar={<AppBar noRadius title='Secret Santa' onBack={onBack} />}>
        <DataIconPlaceholder icon={mdiGiftOutline} text='You were not part of this Secret Santa.' />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      appBar={
        <>
          <AppBar noRadius title='Secret Santa' onBack={onBack} />
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs variant='fullWidth' value={tab} onChange={(_e, v) => setTab(v)} aria-label='Secret Santa tabs'>
              <Tab id='my-pair-tab' aria-controls='my-pair-tabpanel' label='My pair' />
              <Tab id='my-santa-tab' aria-controls='my-santa-tabpanel' label='My wishlist' />
            </Tabs>
          </Box>
        </>
      }
      minusHeight='7rem'
      paddingBottom={false}
      containerSx={{ position: "static" }}
    >
      <TabPanel aria-labelledby='my-pair-tab' id='my-pair-tabpanel' index={0} value={tab}>
        <MyPair
          eventId={timelineEvent!.event!.id}
          groupId={selected.groupId!}
          isAppFocused={isAppFocused}
          readonly
          userAuthId={userAuthId}
          users={timelineEvent?.users ?? []}
        />
      </TabPanel>
      <TabPanel aria-labelledby='my-santa-tab' id='my-santa-tabpanel' index={1} value={tab}>
        <MyWishlist
          eventId={timelineEvent!.event!.id}
          groupId={selected.groupId!}
          isAppFocused={isAppFocused}
          readonly
          userAuthId={userAuthId}
        />
      </TabPanel>
    </PageLayout>
  );
}
